{
  "title": "Sources",
  "description": "Connect existing systems so memory starts with cited answers. What you collect becomes evidence that agents cite and reviewers turn into lessons. Importers manage collection only; reading memory and reviewing lessons need separate access.",
  "newSource": "New source",
  "setUpAnother": "Set up another source",
  "noConnectionsTitle": "No sources connected yet",
  "noConnectionsMessage": "Connect a provider to collect bounded, read-only evidence for one service and environment.",
  "notFoundTitle": "Connection not found",
  "notFoundMessage": "It may have been removed, or your session cannot read it.",
  "fetchFailed": "Could not load the source connections.",
  "detailFetchFailed": "Could not load this connection.",
  "accountsFailed": "Could not load the connector accounts.",
  "planFailed": "The collection plan could not be saved.",
  "actionFailed": "The action could not be completed.",
  "offline": "This browser is offline. Collection controls are disabled until it reconnects.",
  "table": {
    "provider": "Provider",
    "scope": "Scope",
    "account": "Account",
    "status": "Status",
    "lastCollection": "Last collection"
  },
  "row": {
    "lastSuccess": "Last success",
    "neverCollected": "Not collected yet",
    "failedRuns": "{{count}} failed run in 24h",
    "failedRuns_plural": "{{count}} failed runs in 24h",
    "planStale": "Needs review"
  },
  "detail": {
    "reviewTitle": "Review collection plan",
    "collectionTitle": "Source collection",
    "connectionId": "Connection",
    "account": "Account",
    "providerAccount": "Provider account",
    "scope": "Memory scope",
    "access": "Evidence access",
    "lookback": "Lookback",
    "refresh": "Automatic refresh",
    "refreshEvery": "Every {{duration}}",
    "lastSuccess": "Last successful collection",
    "resources": "Selected resources",
    "resourcesUnavailable": "Resource selection is unavailable. Refresh this connection before activating.",
    "limits": "Collection limits",
    "limitsText": "Up to {{pages}} pages, {{sources}} sources and {{bytes}} per collection.",
    "latestRun": "Latest collection",
    "runState": "State",
    "runCompleted": "Completed",
    "runPages": "Committed pages",
    "runDocuments": "Documents processed",
    "runRequests": "Provider requests",
    "runBytes": "Bytes received",
    "runNote": "A failed or partial collection does not prove that absent resources were deleted. Only a complete authoritative inventory can withdraw missing resources.",
    "noRun": "No collection has been recorded. A saved plan is not a verified provider connection.",
    "attentionTitle": "Collection needs attention",
    "attentionText": "{{error}}. Check the account, permissions, and selected resources, then review before resuming.",
    "staleTitle": "This plan needs a new review",
    "staleText": "The account or operator profile changed since this plan was reviewed. Review a new collection plan before collection resumes.",
    "reviewAgain": "Review a new plan",
    "planSavedTitle": "Plan saved for review",
    "planSavedText": "No provider data has been collected. Check the account, resources and scope, then authorize collection.",
    "warnings": "Plan warnings",
    "authorizeTitle": "Authorize collection",
    "resumeTitle": "Resume collection",
    "authorizeText": "Activation authorizes read-only collection now and bounded automatic refresh within this exact configuration. It does not approve or publish lessons.",
    "planHash": "Plan",
    "consent": "I understand collection is read-only, stays within this selection, scope and access class, and refreshes automatically.",
    "activate": "Activate collection",
    "resume": "Resume collection",
    "planOnly": "Your session can plan sources but not activate them. Ask an operator with apply rights to authorize this plan.",
    "sync": "Sync now",
    "pause": "Pause collection",
    "disconnect": "Disconnect source",
    "controlsNote": "Pause stops future collection and retains evidence. Disconnect stops collection and withdraws this connection's evidence and dependent lessons from ordinary reads.",
    "disconnectConfirmTitle": "Disconnect this source?",
    "disconnectConfirmText": "Collection will stop and its evidence and dependent lessons will be withdrawn from ordinary reads. Historical source revisions are withdrawn too.",
    "disconnectConfirmOk": "Disconnect",
    "seconds": "{{count}} seconds",
    "stepSeconds": "{{count}} seconds",
    "collected": "Collected",
    "notCollected": "Not collected",
    "activated": "Collection authorized and queued. Refresh to inspect the collection result.",
    "synced": "Collection queued. Refresh to inspect the result.",
    "paused": "Collection paused. Existing evidence is retained.",
    "disconnected": "Source disconnected. Its evidence has been withdrawn.",
    "conflict": "The plan changed on the server. Review the updated plan before activating."
  },
  "summary": {
    "repository": "Repository",
    "reference": "Branch, tag, or commit",
    "deploymentEnvironment": "Deployment environment",
    "files": "Files",
    "namespace": "Namespace",
    "labelSelector": "Label selector",
    "monitorIds": "Monitor IDs",
    "queries": "Metric queries",
    "serviceIds": "PagerDuty service IDs",
    "serviceLabel": "Service label",
    "environmentLabel": "Environment label",
    "step": "Query step",
    "promql": "PromQL range queries",
    "alertRules": "Alert rules",
    "annotations": "Annotations",
    "annotationsCollected": "Collected for the selected dashboards",
    "dashboardUids": "Dashboard UIDs",
    "feed": "Data",
    "feedDistillation": "Log distillation (MCP)",
    "feedGovernance": "Governance (REST)",
    "app": "App",
    "workflowHistory": "Workflow history",
    "workflowHistoryCollected": "Newest 100 account workflows; only selected agents retained",
    "agentIds": "Agent IDs",
    "policyIds": "Policy IDs",
    "collectionId": "AWS collection ID",
    "collectionRevision": "Collection revision",
    "region": "Region",
    "resourceTypes": "Selected resource kinds"
  },
  "coverage": {
    "github": "Only selected files at the chosen ref and optional deployment records. No repository crawl or workflow logs.",
    "kubernetes": "Deployment and Service metadata for one namespace and label selector. No pod logs, secrets, or environment variables.",
    "datadog": "Selected monitors and bounded metric queries. Empty series and null points do not demonstrate recovery.",
    "pagerduty": "Selected services, incidents created in the collection window, and their bounded log entries. Resolution status does not independently verify recovery.",
    "aws": "Reads only the AWS resources configured by your operator. This is not an account-wide crawl and does not prove runtime dependency use.",
    "prometheus": "Range queries over each collection window and the alert rules you name. Every query must match the exact service and environment labels. Missing points and NaN samples do not demonstrate recovery.",
    "grafana": "The dashboards and Grafana alert rules you name, plus optional annotations on those dashboards. A dashboard shows where a signal is charted. It does not measure the service or verify recovery.",
    "mezmo": "Log distillation reads one app and namespace through Mezmo MCP. Governance reads selected agents and policies through REST; optional history scans the newest 100 account workflows and retains only decisions for the selected agents in the collection window. Decisions do not prove tool execution or grant future permission.",
    "fallback": "Only the reviewed selection is collected."
  },
  "flow": {
    "title": "Set up a source",
    "description": "Map a bounded selection to one service and environment. Planning checks this configuration and permissions; it does not contact the provider or collect evidence.",
    "review": "Review collection plan",
    "reviewing": "Preparing plan…",
    "fixErrors": "Fix the highlighted fields to continue.",
    "steps": {
      "provider": {
        "title": "Provider",
        "hint": "Where the evidence comes from"
      },
      "account": {
        "title": "Account",
        "hint": "A configured, read-only credential"
      },
      "resources": {
        "title": "Resources",
        "hint": "What to collect"
      },
      "scope": {
        "title": "Scope and refresh",
        "hint": "Where it lands and how often"
      }
    },
    "provider": {
      "eyebrow": "Step 1 of 4",
      "title": "Choose a provider",
      "description": "Each provider collects a narrow, read-only selection. The coverage note says exactly what is and is not read."
    },
    "account": {
      "eyebrow": "Step 2 of 4",
      "title": "Choose an account",
      "description": "Accounts are configured by an administrator. Credentials and provider URLs stay on the server.",
      "label": "Account",
      "choose": "Choose an account",
      "collectsFor": "Collects for {{scopes}}.",
      "none": "No {{provider}} account is available in your scopes. Ask an administrator to add one under Connector accounts.",
      "unavailable": "The account list is unavailable. Enter the account ID an administrator gave you.",
      "idLabel": "Account ID",
      "idHelp": "Ask an administrator for a configured account ID.",
      "fromProfile": "operator profile",
      "manageLink": "Add an account"
    },
    "resources": {
      "eyebrow": "Step 3 of 4",
      "title": "Choose what to collect",
      "description": "Name the exact resources. Everything else in the provider stays unread."
    },
    "scope": {
      "eyebrow": "Step 4 of 4",
      "title": "Map to a memory scope",
      "description": "Collected evidence lands in one service and environment with one access class, and refreshes on a pinned window.",
      "serviceHelp": "The memory service name, for example checkout-api. Letters, numbers, dots, dashes and underscores.",
      "environmentHelp": "The memory environment, for example prod.",
      "accessClass": "Evidence access",
      "accessClassHelp": "Who may read the collected evidence. Only classes your session may assign are listed.",
      "chooseAccess": "Choose access",
      "lookback": "Lookback",
      "lookbackHelp": "How far back each collection reads. 1 minute to 31 days; each refresh uses a pinned collection window. Mezmo log distillation allows at most 14 days.",
      "interval": "Refresh interval",
      "intervalHelp": "How often collection repeats. 1 minute to 31 days.",
      "units": {
        "minutes": "minutes",
        "hours": "hours",
        "days": "days"
      }
    },
    "fields": {
      "owner": "Repository owner",
      "repository": "Repository name",
      "reference": "Branch, tag, or commit",
      "fileKind": "Selected file type",
      "fileKinds": {
        "runbook": "Runbook",
        "catalog": "Service catalog",
        "deployment_config": "Deployment configuration"
      },
      "files": "Relative file paths",
      "filesHelp": "One path per line, up to 24 files of the selected type.",
      "deploymentEnvironment": "GitHub deployment environment",
      "deploymentEnvironmentHelp": "Optional. Leave blank to collect files only.",
      "namespace": "Namespace",
      "labelSelector": "Label selector",
      "labelSelectorHelp": "Required to bound the inventory. Deployment and Service metadata only.",
      "monitorIds": "Monitor IDs",
      "monitorIdsHelp": "One positive numeric ID per line, up to 24.",
      "queries": "Metric queries",
      "queriesHelp": "Up to 3 queries, one per line. Each must select the exact service and env tags; no wildcards.",
      "serviceIds": "PagerDuty service IDs",
      "serviceIdsHelp": "One ID per line, up to 8 services explicitly mapped to this service and environment.",
      "promql": "PromQL range queries",
      "promqlHelp": "Up to 3 queries, one per line. Every metric selector must match the exact service and environment labels below.",
      "stepSeconds": "Query step in seconds",
      "stepSecondsHelp": "15 to 3600. Long windows use a coarser step so each series keeps at most 1000 points.",
      "alertRules": "Alert rule names",
      "alertRulesHelp": "Up to 24 alerting rules, one per line. Only alerts labelled with this service and environment are kept.",
      "serviceLabel": "Service label",
      "serviceLabelHelp": "The label that carries the service name on your series and alerts.",
      "environmentLabel": "Environment label",
      "environmentLabelHelp": "The label that carries the environment.",
      "dashboardUids": "Dashboard UIDs",
      "dashboardUidsHelp": "Up to 12 dashboards, one UID per line. The UID is the part of the dashboard URL after /d/.",
      "annotations": "Collect dashboard annotations in each window",
      "grafanaAlertRulesHelp": "Up to 24 Grafana alert rules, one per line. Only alerts labelled with this service and environment are kept.",
      "mezmoFeed": "Mezmo data",
      "mezmoApp": "Mezmo app",
      "mezmoAppHelp": "One exact app mapped to this memory scope. Choose the account configured for mcp.mezmo.com/mcp.",
      "mezmoNamespace": "Mezmo namespace",
      "mezmoNamespaceHelp": "One exact namespace. Collects error changes and up to 10 masked patterns; raw log examples are excluded.",
      "agentIds": "Mezmo agent IDs",
      "agentIdsHelp": "Up to 8 exact IDs, one per line. Choose the account configured for api.mezmo.com.",
      "policyIds": "Mezmo policy IDs",
      "policyIdsHelp": "Up to 8 exact IDs, one per line. Policy summaries omit argument conditions; follow the source to inspect the full policy.",
      "workflowHistory": "Collect bounded recent agent workflow history",
      "collectionId": "AWS collection ID",
      "collectionIdHelp": "Collection identifier from your operator. It identifies a configured AWS selection.",
      "collectionRevision": "Collection revision",
      "collectionRevisionHelp": "The sha256 revision supplied by your operator for this exact collector configuration.",
      "region": "AWS region",
      "resourceTypes": "AWS resource kinds",
      "resourceTypesHelp": "Choose the kinds the operator's collection includes."
    },
    "awsTypes": {
      "ec2_instance": "EC2 instance",
      "ec2_vpc": "EC2 VPC",
      "ec2_subnet": "EC2 subnet",
      "ec2_security_group": "EC2 security group",
      "ec2_route_table": "EC2 route table",
      "elbv2_load_balancer": "Elastic Load Balancing v2 load balancer",
      "elbv2_target_group": "Elastic Load Balancing v2 target group",
      "rds_instance": "RDS instance",
      "rds_cluster": "RDS cluster",
      "elasticache_cluster": "ElastiCache cluster",
      "elasticache_replication_group": "ElastiCache replication group",
      "dynamodb_table": "DynamoDB table",
      "lambda_function": "Lambda function",
      "ecs_cluster": "ECS cluster",
      "ecs_service": "ECS service",
      "sqs_queue": "SQS queue",
      "sns_topic": "SNS topic",
      "eventbridge_rule": "EventBridge rule",
      "eks_cluster": "EKS cluster"
    }
  },
  "validation": {
    "required": "This field is required.",
    "account": "Choose the account to collect with.",
    "provider": "Choose a supported provider.",
    "files": "List 1 to 24 file paths, one per line.",
    "monitorIds": "Monitor IDs must be distinct positive whole numbers, up to 24.",
    "queries": "Choose up to 3 queries of at most 1000 characters each.",
    "datadogEmpty": "Add at least one monitor ID or metric query.",
    "serviceIds": "Choose 1 to 8 distinct PagerDuty service IDs using letters and numbers.",
    "collectionId": "Start with a letter or number and use only letters, numbers, underscores, or hyphens (up to 64 characters).",
    "collectionRevision": "Use sha256 followed by 64 lowercase hexadecimal characters.",
    "region": "AWS region must be 1 to 32 characters.",
    "resourceTypes": "Choose at least one AWS resource kind.",
    "promql": "Choose up to 3 PromQL queries of at most 1000 characters each.",
    "prometheusEmpty": "Add at least one PromQL query or alert rule name.",
    "stepSeconds": "Query step must be a whole number from 15 to 3600 seconds.",
    "alertRules": "Choose up to 24 distinct alert rule names.",
    "scopeLabels": "Service and environment labels must be two different label names, such as service and env.",
    "dashboardUids": "Choose up to 12 distinct dashboard UIDs using letters, numbers, hyphens, or underscores.",
    "grafanaEmpty": "Add at least one dashboard UID or alert rule name.",
    "annotationsNeedDashboards": "Annotations are collected only for selected dashboards.",
    "mezmoExact": "Use one exact name: letters, numbers, dots, dashes and underscores, up to 100 characters.",
    "mezmoLookback": "Mezmo log distillation supports a lookback of at most 14 days.",
    "mezmoIds": "Choose up to 8 distinct 24-character hexadecimal IDs.",
    "mezmoEmpty": "Choose an agent or policy.",
    "mezmoHistoryNeedsAgents": "Workflow history requires selected agents.",
    "scopeValue": "Use letters, numbers, dots, dashes and underscores, up to 100 characters.",
    "accessClass": "Choose an evidence access class.",
    "duration": "Use a whole number between 1 minute and 31 days."
  }
}
